import { useCallback, useRef, useState } from 'react';
import { describeDbError } from '../lib/errors';

export interface SubmitState {
  submitting: boolean;
  error: string | null;
  clearError: () => void;
  /**
   * Runs the action, capturing any error as a message. Resolves to true when
   * it succeeded, so callers can close a sheet or navigate only on success.
   */
  run: (action: () => Promise<unknown>) => Promise<boolean>;
}

/**
 * Shared plumbing for every write in the app: a pending flag, an error
 * message, and a guard against double submission.
 *
 * The guard is not theoretical. Tapping "Save" twice on a slow connection
 * writes the expense twice, and there is nothing in the schema to stop it —
 * two identical dinners are perfectly legal rows.
 */
export function useSubmit(): SubmitState {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const run = useCallback(async (action: () => Promise<unknown>) => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);

    try {
      await action();
      return true;
    } catch (err) {
      setError(describeDbError(err));
      return false;
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }, []);

  return { submitting, error, clearError: useCallback(() => setError(null), []), run };
}
